import PropTypes from 'prop-types';

import { ContainerProps as MuiContainerProps } from '@mui/material/Container';
import { Header } from '@classity-edtech/ui-base/dist/components/Displays';
import { Navigation } from '@classity-edtech/ui-base/dist/components/Displays/Navigation/Navigation';
import { Box, Typography } from '@mui/material';
import { AUTH_KEY } from '@classity-edtech/ui-base';
import { clearAuthCookie, getCookieValue } from '@classity-edtech/ui-base';
import { useEffect, useState } from 'react';
import { CircularProgress } from '@classity-edtech/ui-base';
import { useLocation, useNavigate } from 'react-router-dom';
import { webPaths } from '../../routes/paths';
import { Roles } from '@classity-edtech/platform-utils';
import { useGetMyProfileQuery } from '../../services/users';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { useLogoutMutation } from '../../services/authentication';

export interface DashboardLayoutProps extends MuiContainerProps {
  profileDropdown?: {
    memberId: string;
    logoutAction: () => void;
  };
}

const DashboardLayout = ({ className, children, profileDropdown }: DashboardLayoutProps) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [openSidebar, setOpenSidebar] = useState(false);
  const [logout] = useLogoutMutation();

  const {
    data: user,
    isLoading: loadingUser,
    isUninitialized: uninitialisedUser,
  } = useGetMyProfileQuery();

  const toggleOpenSidebar = () => {
    setOpenSidebar(!openSidebar);
  };

  const getActiveNav = () => {
    switch (pathname) {
      case `/${webPaths.applications}`:
        return 'Applications';
      case `/${webPaths.adminDashboard}`:
      case `/${webPaths.tutorDashboard}`:
      case `/${webPaths.learnerDashboard}`:
        return 'Dashboard';
      case `/${webPaths.adminTutors}`:
        return 'Tutors';
      case `/${webPaths.tutorDashboard}`:
        return 'Teach';
      case `/${webPaths.tutork12}`:
        return 'K-12';
      case `/${webPaths.tutorPrivateTuition}`:
        return 'Private Tuition';
      case `/${webPaths.tutorPerformance}`:
        return 'Performance';
      case `/${webPaths.tutorPayments}`:
        return 'Revenue';
      case `/${webPaths.tutorTuitions}`:
      case `/${webPaths.studentTuitions}`:
        return 'Tuition';

      default:
        return '';
    }
  };

  const getDashboardNavigation = () => {
    if (pathname.includes('/a')) {
      return navigate(`/${webPaths.adminDashboard}`);
    }
    if (pathname.includes('/t')) {
      return navigate(`/${webPaths.tutorDashboard}`);
    }
    if (pathname.includes('/s')) {
      return navigate(`/${webPaths.learnerDashboard}`);
    }
  };

  const getRoleMenu = () => {
    if (user?.roleId === Roles.ADMIN) {
      return {
        title: { name: 'Admin' },
        items: [
          {
            name: 'Applications',
            icon: 'User',
            onClick: () => navigate(`/${webPaths.applications}`),
          },
          {
            name: 'Tutors',
            icon: 'DashboardIcon',
            onClick: () => navigate(`/${webPaths.adminTutors}`),
          },
        ],
      };
    }
    if (user?.roleId === Roles.TUTOR || user?.roleId === Roles.PROSPECT_TUTOR) {
      return {
        title: { name: 'Teach', onClick: () => navigate(`/${webPaths.tutorDashboard}`) },
        items: [
          {
            name: 'Group Class',
            icon: 'UsersIcon',
            onClick: () => navigate(`/${webPaths.tutork12}`),
          },
          {
            name: 'Private Tuition',
            icon: 'User',
            onClick: () => navigate(`/${webPaths.tutorPrivateTuition}`),
          },
          {
            name: 'Performance',
            icon: 'PerformanceIcon',
            onClick: () => navigate(`/${webPaths.tutorPerformance}`),
          },
          {
            name: 'Revenue',
            icon: 'RevenueIcon',
            onClick: () => navigate(`/${webPaths.tutorPayments}`),
          },
        ]?.filter(Boolean),
      };
    }
    if (user?.roleId === Roles.STUDENT) {
      return {
        // title: { name: 'Learn', onClick: () => navigate(`/${webPaths.tutorDashboard}`) },
        // items: [
        //   {
        //     name: 'Private Tuition',
        //     icon: 'User',
        //     onClick: () => navigate(`/${webPaths.studentTuitions}`),
        //   },
        // ],
      };
    }
    return {};
  };

  useEffect(() => {
    if (!user && !loadingUser && !uninitialisedUser) {
      localStorage.removeItem('token');
      navigate(`${webPaths.root}`, { replace: true });
    }
  }, [user]);

  const memberProfile = {
    ...(profileDropdown || {}),
    memberId: user?.profile?.ids?.find(p => p?.name === 'uid')?.value || '',
    logoutAction: () => {
      const clientId = getCookieValue(AUTH_KEY);

      logout({ clientId });

      clearAuthCookie();

      navigate(`${webPaths.root}`);
    },
  };

  return (
    <>
      <Box className={className}>
        <Header
          sx={{ px: { xs: 2.5, md: 4 } }}
          handleMenuClick={toggleOpenSidebar}
          profileDropdown={memberProfile}
        />

        {/* Main Content */}
        {loadingUser || uninitialisedUser ? (
          <Box
            sx={{
              width: '100%',
              height: '100dvh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              gap: 4,
              mt: { xs: 2, md: 6 },
              px: { xs: 2.5, md: 4 },
              minHeight: { xs: 'calc(100dvh - 130px)', md: 'calc(100dvh - 165px)' },
            }}
          >
            <Navigation
              openSidebar={openSidebar}
              closeSidebar={toggleOpenSidebar}
              sx={{
                display: { xs: 'none', md: 'flex' },
                width: { xs: '100%', md: '145px' },
              }}
              activeNav={getActiveNav()}
              height={'calc(100dvh - 168px)'}
              sections={[
                {
                  title: { name: '' },
                  items: [
                    {
                      name: 'Dashboard',
                      icon: 'DashboardIcon',
                      onClick: () => getDashboardNavigation(),
                    },
                  ],
                },
                {
                  title: { name: '' },
                  items: [
                    {
                      name: '',
                      onClick: () => void 0,
                    },
                  ],
                },
                // user?.roleId !== Roles.ADMIN
                //   ? {
                //       title: { name: '' },
                //       items: [
                //         {
                //           name: 'Refer a friend',
                //           icon: 'ReferralIcon',
                //           onClick: () => {},
                //         },
                //       ],
                //     }
                //   : undefined,
                getRoleMenu()?.title?.name ? getRoleMenu() : ({ name: '', items: [] } as any),
              ]?.filter(Boolean)}
              footer={[
                {
                  name: 'Nigeria',
                  icon: 'GlobalIcon',
                  onClick: () => {},
                },
              ]}
              search={{
                value: '',
                onChange: e => {},
                placeHolder: 'Search Classity...',
              }}
            />

            <Box sx={{ width: '100%' }}>{children}</Box>
          </Box>
        )}

        {/* Footer */}
        <Box
          sx={{
            py: 1,
            px: { xs: 2.5, md: 4 },
            background: globalColours['$global-colour-secondary-brand-4'],
          }}
        >
          <Typography variant='body3' sx={{ color: globalColours['$global-colour-base-contrast'] }}>
            © 2026 Classity EdTech Ltd. All rights reserved.{' '}
          </Typography>
        </Box>
      </Box>
    </>
  );
};

DashboardLayout.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

DashboardLayout.defaultProps = {
  children: null,
  className: null,
};

export default DashboardLayout;
